import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { PersistentErrorService, PersistentError } from '../../core/services/persistent-error.service';

@Component({
  selector: 'app-persistent-error-display',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="persistent-errors-container" *ngIf="hasErrors">
      <div class="persistent-error" 
           *ngFor="let error of errors; trackBy: trackByErrorId"
           [class]="'error-' + error.severity">
        
        <!-- Error Icon -->
        <div class="error-icon">
          <i [class]="getErrorIcon(error.severity)"></i>
        </div>
        
        <!-- Error Content -->
        <div class="error-content">
          <div class="error-summary">{{ error.summary }}</div>
          <div class="error-detail">{{ error.detail }}</div>
          <div class="error-timestamp">{{ formatTimestamp(error.timestamp) }}</div>
        </div>
        
        <!-- Close Button -->
        <button *ngIf="error.closable" 
                class="error-close-btn"
                (click)="removeError(error.id)"
                [attr.aria-label]="'Close error message'"
                type="button">
          <i class="pi pi-times"></i>
        </button>
      </div>
    </div>
  `,
  styles: [`
    .persistent-errors-container {
      position: fixed;
      top: 80px;
      right: 20px;
      z-index: 9999;
      max-width: 400px;
      width: 100%;
      pointer-events: none;
    }

    .persistent-error {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 16px;
      margin-bottom: 12px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      pointer-events: auto;
      animation: slideInRight 0.3s ease-out;
      position: relative;
      overflow: hidden;
    }

    .persistent-error::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
    }

    .error-error {
      background-color: #fef2f2;
      border: 1px solid #fecaca;
      color: #991b1b;
    }

    .error-error::before {
      background-color: #dc2626;
    }

    .error-warn {
      background-color: #fffbeb;
      border: 1px solid #fed7aa;
      color: #92400e;
    }

    .error-warn::before {
      background-color: #f59e0b;
    }

    .error-info {
      background-color: #eff6ff;
      border: 1px solid #bfdbfe;
      color: #1e40af;
    }

    .error-info::before {
      background-color: #3b82f6;
    }

    .error-icon {
      flex-shrink: 0;
      margin-top: 2px;
    }

    .error-icon i {
      font-size: 18px;
    }

    .error-content {
      flex: 1;
      min-width: 0;
    }

    .error-summary {
      font-weight: 600;
      font-size: 14px;
      margin-bottom: 4px;
      line-height: 1.4;
    }

    .error-detail {
      font-size: 13px;
      line-height: 1.4;
      margin-bottom: 6px;
      word-wrap: break-word;
    }

    .error-timestamp {
      font-size: 11px;
      opacity: 0.7;
      font-style: italic;
    }

    .error-close-btn {
      flex-shrink: 0;
      background: none;
      border: none;
      padding: 4px;
      cursor: pointer;
      border-radius: 4px;
      transition: background-color 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
    }

    .error-close-btn:hover {
      background-color: rgba(0, 0, 0, 0.1);
    }

    .error-close-btn i {
      font-size: 12px;
    }

    @keyframes slideInRight {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    /* Mobile responsiveness */
    @media (max-width: 768px) {
      .persistent-errors-container {
        top: 60px;
        right: 10px;
        left: 10px;
        max-width: none;
      }
      
      .persistent-error {
        padding: 12px;
        margin-bottom: 8px;
      }
      
      .error-summary {
        font-size: 13px;
      }
      
      .error-detail {
        font-size: 12px;
      }
    }
  `]
})
export class PersistentErrorDisplayComponent implements OnInit, OnDestroy {
  errors: PersistentError[] = [];
  hasErrors = false;
  private subscription?: Subscription;

  constructor(private persistentErrorService: PersistentErrorService) {}

  ngOnInit(): void {
    this.subscription = this.persistentErrorService.errors$.subscribe(errors => {
      this.errors = errors;
      this.hasErrors = errors.length > 0;
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  removeError(errorId: string): void {
    this.persistentErrorService.removeError(errorId);
  }

  trackByErrorId(index: number, error: PersistentError): string {
    return error.id;
  }

  getErrorIcon(severity: string): string {
    switch (severity) {
      case 'error':
        return 'pi pi-exclamation-triangle';
      case 'warn':
        return 'pi pi-exclamation-circle';
      case 'info':
        return 'pi pi-info-circle';
      default:
        return 'pi pi-info-circle';
    }
  }

  formatTimestamp(timestamp: Date): string {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) {
      return 'Just now';
    } else if (minutes < 60) {
      return `${minutes}m ago`;
    } else {
      const hours = Math.floor(minutes / 60);
      if (hours < 24) {
        return `${hours}h ago`;
      } else {
        return timestamp.toLocaleDateString();
      }
    }
  }
}
